import { Injectable, inject } from '@angular/core';

import { SearchResponseModel } from '../models/search-response.model';
import { BaseStore } from './base-store';
import { TableStateRepository } from '../../services/repositories/table-state.repository';
import { UnsyncedChangesRepository } from '../../services/repositories/unsynced-changes.repository';
import { CrudOperationEnum } from '../../models/enums/crud-operation.enum';
import { BaseEntityModel } from './base-entity.model';

/**
 * Adds offline change-tracking on top of the raw persistence provided by
 * {@link BaseStore}.
 *
 * Both collaborators are obtained via Angular DI (`inject()` field
 * initializers) instead of the former `StaticReferences` service locator, so
 * every repository's dependencies are visible in the type and can be replaced
 * with test doubles through a TestBed provider (issue #10).
 *
 * Injecting these eagerly is safe because {@link UnsyncedChangesRepository} and
 * {@link TableStateRepository} extend {@link BaseStore} (not `BaseRepository`),
 * so constructing them does not re-enter these injections — no NG0200 cycle.
 */
@Injectable()
export abstract class BaseRepository<T extends BaseEntityModel> extends BaseStore<T> {
    protected readonly unsyncedChangesRepository = inject(UnsyncedChangesRepository);
    protected readonly tableStateRepository = inject(TableStateRepository);

    protected skipLocalUpdate = false;

    abstract sync(data: SearchResponseModel<T>): Promise<void>;

    protected async markAsChangeRange(
        operation: CrudOperationEnum,
        records?: T[],
        recordsIds?: string[],
        skipLocalUpdate: boolean = this.skipLocalUpdate
    ): Promise<void> {
        if (skipLocalUpdate) {
            return;
        }

        if (records) {
            for (const record of records) {
                await this.markAsChange(operation, record, undefined, skipLocalUpdate);
            }
        }

        if (recordsIds) {
            for (const recordId of recordsIds) {
                await this.markAsChange(operation, undefined, recordId, skipLocalUpdate);
            }
        }
    }

    protected async markAsChange(
        operation: CrudOperationEnum,
        record?: T,
        recordId?: string,
        skipLocalUpdate: boolean = this.skipLocalUpdate
    ): Promise<void> {
        if (skipLocalUpdate) {
            return;
        }

        await this.updateModifiedDate();

        // The read-decide-write coalescing is delegated to a single atomic
        // transaction on the unsyncedChanges store so concurrent edits to the
        // same record can no longer race. Using `record?.id ?? recordId` as the
        // lookup key means delete-after-create/update also coalesces correctly.
        await this.unsyncedChangesRepository.applyCoalesced(
            this.config.tableName,
            operation,
            record,
            recordId
        );
    }

    private async updateModifiedDate(): Promise<void> {
        const tableState = await this.tableStateRepository.getById(this.config.tableName);
        if (!tableState) {
            return;
        }

        const now = new Date();
        const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
            now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
        tableState.lastLocalUpdate = new Date(nowUtc);

        await this.tableStateRepository.edit(tableState);
    }
}
